import React, { useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM, TitleSM, TitleXXS } from '@/atoms/Text'
import { useThemeContext } from '@/contexts/ThemeContext'
import { useSaleBanner } from '@/services/ContentfulSaleService/hooks'
import { useSafeTrack } from '@/utils/analytics'
import { GUILD_SALE_BANNER_DISMISSED, getObjectFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildPromotionCountdown } from '@/views/GuildSignupView/GuildPromotionBanner/GuildPromotionCountdown'

type DismissedSales = Record<string, boolean>

const GUILD_FUNNEL_PATH_REGEXES = [/\/guild.*/]

export const SaleBannerNew: React.FC = () => {
  const { t } = useTranslate('common')
  const track = useSafeTrack()
  const router = useSafeRouter()
  const { saleWithBanner: sale, loading } = useSaleBanner()
  const { isDarkMode } = useThemeContext()
  const [isDismissed, setIsDismissed] = useState(false)
  const isGuildFunnelPath = GUILD_FUNNEL_PATH_REGEXES.some((r) => r.test(router.asPath))

  const saleId = sale?.id ?? ''

  // Check if this sale has been dismissed (computed during render to avoid flash)
  const isDismissedInStorage = useMemo(() => {
    if (!saleId) return false
    const dismissedSales = getObjectFromLocalStorage<DismissedSales>(GUILD_SALE_BANNER_DISMISSED) ?? {}
    return Boolean(dismissedSales[saleId])
  }, [saleId])

  const handleSaleBannerClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      track('Sale Banner Clicked', { saleId, sale })

      const planSelectionElement = document.getElementById('plan-selection')

      // If plan-selection exists, scroll to it, otherwise, let the href handle navigation
      if (planSelectionElement) {
        e.preventDefault()
        planSelectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    [track, saleId, sale],
  )

  const handleDismiss = useCallback(() => {
    if (!saleId) return
    track('Sale Banner Dismissed', { saleId, sale })

    const dismissedSales = getObjectFromLocalStorage<DismissedSales>(GUILD_SALE_BANNER_DISMISSED) ?? {}
    dismissedSales[saleId] = true
    writeToLocalStorage(GUILD_SALE_BANNER_DISMISSED, dismissedSales)

    setIsDismissed(true)
  }, [saleId, sale, track])

  const isExpired = useMemo(() => {
    if (!sale?.endDate) return false
    const endDate = new Date(sale.endDate)
    return Number.isFinite(endDate.valueOf()) && endDate < new Date()
  }, [sale?.endDate])

  if (!sale || isDismissed || isDismissedInStorage || !sale.showSaleBanner || isExpired || loading) return null

  return (
    <>
      {/* desktop only version */}
      <div
        className={classNames('relative z-20 hidden min-h-[88px] w-full bg-white lg:block', {
          'border border-x-0 border-core-gray-200': !isDarkMode,
        })}
      >
        <Image
          className="absolute inset-y-0 left-0 h-full"
          alt="black friday banner image left"
          width={243}
          height={88}
          src={'/v1760380230/angel-studios/sale-banner-shows-left.webp'}
        />
        <Image
          className={classNames('absolute inset-y-0 hidden right-0 h-full xl:block', {
            'right-[90px]': !isGuildFunnelPath,
          })}
          alt="black friday banner image right"
          width={243}
          height={88}
          src={'/v1760380234/angel-studios/sale-banner-shows-right.webp'}
        />
        <div className="absolute h-full w-full md:block">
          <div
            className={classNames(
              'flex h-full items-center justify-between gap-x-6 pl-64 pr-[30px] lg:pr-[125px] xl:pr-[300px]',
              {
                'pr-[120px] xl:pr-[390px]': !isGuildFunnelPath,
              },
            )}
          >
            <div className="flex shrink-0 flex-col justify-center gap-1">
              <div className="flex">
                <TitleSM weight="bold" className="xl:photon-title-md uppercase">
                  {sale.name}
                </TitleSM>
              </div>
              {sale.endDate && (
                <div className="flex items-center gap-4">
                  <CaptionSM className="uppercase" weight="bold" color="black">
                    <Translate i18nKey="endsIn" t={t}>
                      Ends In
                    </Translate>
                  </CaptionSM>
                  <GuildPromotionCountdown endDate={sale.endDate} />
                </div>
              )}
            </div>
            <div className="flex items-center justify-center">
              <TitleXXS color="black" weight="semibold" className="xl:photon-title-xs">
                {sale.pitch}
              </TitleXXS>
            </div>
            <LinkButton
              href="/guild/join"
              variant="gray"
              size="sm"
              outline={false}
              onClick={handleSaleBannerClick}
              className="shrink-0"
            >
              <span className="photon-button-md font-bold">
                <Translate i18nKey="learnMore" t={t}>
                  Learn More
                </Translate>
              </span>
            </LinkButton>
          </div>
          <When condition={!isGuildFunnelPath}>
            <div className="absolute right-0 top-7 h-full shrink-0 pr-6">
              <button
                type="button"
                onClick={handleDismiss}
                className="group flex items-center gap-2 rounded-full py-2 text-core-gray-600 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-core-gray-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white hover:text-black"
                aria-label={t('closeSaleBannerAriaLabel', 'Close sale banner')}
              >
                <TitleXXS className="tracking-wide" color="black">
                  <Translate i18nKey="close" t={t}>
                    Close
                  </Translate>
                </TitleXXS>
                <CloseIcon size={20} color="black" />
              </button>
            </div>
          </When>
        </div>
      </div>
    </>
  )
}
